import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../styles/quiz.css';

function authHeaders() {
  const token = typeof window !== 'undefined' ? localStorage.getItem('adeeb-token') : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const STAGES = { LOADING: 'loading', PLAYING: 'playing', RESULT: 'result', ERROR: 'error' };

function shuffle(arr) {
  const out = arr.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

export default function Quiz() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [stage, setStage] = useState(STAGES.LOADING);
  const [errorMsg, setErrorMsg] = useState('');
  const [lang, setLang] = useState('en');
  const [data, setData] = useState(null);            // { title, lang, questions }
  const [order, setOrder] = useState([]);            // shuffled question indices
  const [step, setStep] = useState(0);               // 0..N-1
  const [picked, setPicked] = useState(null);        // selected key for current question
  const [checking, setChecking] = useState(false);
  const [feedback, setFeedback] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const [revealed, setRevealed] = useState(false);   // showing right/wrong
  const [scoreKeys, setScoreKeys] = useState([]);    // list of picked keys per question

  const fetchQuiz = useCallback(async (l) => {
    setStage(STAGES.LOADING); setErrorMsg('');
    try {
      const { data } = await axios.get(`/api/books/${id}/quiz`, {
        params: { lang: l },
        headers: authHeaders(),
        timeout: 10000,
      });
      setData(data);
      setOrder(shuffle(data.questions.map((_, i) => i)));
      setStep(0); setPicked(null); setRevealed(false); setScoreKeys([]);
      setCorrectCount(0); setFeedback(false); setChecking(false);
      setStage(STAGES.PLAYING);
    } catch (err) {
      setErrorMsg(err?.response?.data?.message || 'Could not load the quiz.');
      setStage(STAGES.ERROR);
    }
  }, [id]);

  useEffect(() => { fetchQuiz(lang); }, [fetchQuiz, lang]);

  const total = data?.questions?.length || 0;
  const currentQ = data && order.length ? data.questions[order[step]] : null;

  const choose = async (key) => {
    if (revealed || checking) return;
    setChecking(true); setErrorMsg('');
    try {
      const { data: result } = await axios.post(`/api/books/${id}/quiz/check`, { index: order[step], answer: key, lang }, { headers: authHeaders() });
      setPicked(key); setFeedback(result.correct); setRevealed(true);
      setCorrectCount(n => n + (result.correct ? 1 : 0)); setScoreKeys(prev => [...prev, key]);
    } catch { setErrorMsg('Could not check this answer. Please try again.'); }
    finally { setChecking(false); }
  };

  const next = async () => {
    if (checking) return;
    if (step + 1 >= total) {
      setChecking(true); setErrorMsg('');
      const answers = new Array(total); scoreKeys.forEach((key, i) => { answers[order[i]] = key; });
      try {
        const { data: result } = await axios.post(`/api/quizzes/${id}`, { answers, lang }, { headers: authHeaders(), timeout: 10000 });
        setCorrectCount(result.lastScore); setStage(STAGES.RESULT);
      } catch { setErrorMsg('Could not submit the quiz. Please try again.'); }
      finally { setChecking(false); }
      return;
    }
    setStep(s => s + 1); setPicked(null); setRevealed(false);
  };

  // Keyboard: 1-4 picks, Enter advances after reveal
  useEffect(() => {
    if (stage !== STAGES.PLAYING || !currentQ) return;
    const handler = (e) => {
      if (['1','2','3','4'].includes(e.key) && !revealed) {
        const idx = parseInt(e.key, 10) - 1;
        const opt = currentQ.options[idx];
        if (opt) choose(opt.key);
      } else if (e.key === 'Enter' && revealed) {
        next();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  const restart = () => fetchQuiz(lang);

  const isRtl = data?.lang === 'ar';
  const t = (en, ar) => isRtl ? ar : en;

  if (stage === STAGES.LOADING) {
    return (
      <div className="quiz-wrapper">
        <div className="quiz-orb quiz-orb-1" aria-hidden="true" />
        <div className="quiz-loading">
          <div className="quiz-spinner" />
          <p>Loading quiz…</p>
        </div>
      </div>
    );
  }

  if (stage === STAGES.ERROR) {
    return (
      <div className="quiz-wrapper">
        <div className="quiz-error">
          <h2>Couldn’t load the quiz.</h2>
          <p>{errorMsg}</p>
          <div className="quiz-actions">
            <button className="quiz-btn" onClick={() => fetchQuiz(lang)}>Try again</button>
            <Link className="quiz-btn quiz-btn-ghost" to={`/read/${id}`}>Back to book</Link>
            <Link className="quiz-btn quiz-btn-ghost" to="/">Homepage</Link>
          </div>
        </div>
      </div>
    );
  }

  if (stage === STAGES.RESULT) {
    const pct = total > 0 ? Math.round((correctCount / total) * 100) : 0;
    let title, subtitle;
    if (pct === 100)      { title = t('Perfect score!', 'علامة كاملة!');           subtitle = t('Every answer correct.', 'كل إجابة صحيحة.'); }
    else if (pct >= 80)   { title = t('Excellent!', 'ممتاز!');                     subtitle = t('You really know this story.', 'أنت تعرف هذه القصة جيدًا.'); }
    else if (pct >= 60)   { title = t('Good effort!', 'جهد جيد!');                  subtitle = t('A little more practice and you’ll have it.', 'القليل من المراجعة وستتقنها.'); }
    else                  { title = t('Worth another go.', 'جرّب مرة أخرى.');        subtitle = t('Re-read the story and try the quiz again.', 'اقرأ القصة مرة أخرى وحاول مرة أخرى.'); }
    return (
      <div className="quiz-wrapper" dir={isRtl ? 'rtl' : 'ltr'}>
        <div className="quiz-orb quiz-orb-1" aria-hidden="true" />
        <div className="quiz-orb quiz-orb-2" aria-hidden="true" />
        <div className="quiz-result">
          <div className="quiz-eyebrow"><span className="quiz-ornament">&#10040;</span>{t('Quiz complete', 'اكتمل الاختبار')}<span className="quiz-ornament">&#10040;</span></div>
          <div className="quiz-result-ring" aria-hidden="true" style={{ '--pct': pct }}>
            <div className="quiz-result-ring-fill" />
            <div className="quiz-result-ring-num"><span>{correctCount}</span><span className="quiz-result-ring-out">/ {total}</span></div>
          </div>
          <h2 className="quiz-result-title">{title}</h2>
          <p className="quiz-result-sub">{subtitle}</p>
          <div className="quiz-actions">
            <button className="quiz-btn quiz-btn-primary" onClick={restart}>{t('Try again', 'حاول مرة أخرى')}</button>
            <Link className="quiz-btn quiz-btn-ghost" to={`/read/${id}`}>{t('Back to book', 'عودة إلى الكتاب')}</Link>
            <button className="quiz-btn quiz-btn-ghost" onClick={() => navigate('/category/all')}>{t('Library', 'المكتبة')}</button>
          </div>
        </div>
      </div>
    );
  }

  // PLAYING
  const pct = total > 0 ? ((step) / total) * 100 : 0;
  return (
    <div className="quiz-wrapper" dir={isRtl ? 'rtl' : 'ltr'}>
      <div className="quiz-orb quiz-orb-1" aria-hidden="true" />
      <div className="quiz-orb quiz-orb-2" aria-hidden="true" />
      <div className="quiz-inner">
        <header className="quiz-head">
          <div className="quiz-head-row">
            <button
              type="button"
              onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
              className="quiz-back"
              aria-label={t('Back', 'رجوع')}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <polyline points={isRtl ? '10 18 16 12 10 6' : '14 6 8 12 14 18'} />
              </svg>
              <span>{t('Back', 'رجوع')}</span>
            </button>
            <div className="quiz-lang">
              <button type="button" className={`quiz-lang-btn ${lang === 'en' ? 'is-active' : ''}`} onClick={() => setLang('en')}>EN</button>
              <button type="button" className={`quiz-lang-btn ${lang === 'ar' ? 'is-active' : ''}`} onClick={() => setLang('ar')}>ع</button>
            </div>
          </div>
          <h1 className="quiz-title">{data.title}</h1>
          <div className="quiz-progress" role="progressbar" aria-valuemin={0} aria-valuemax={total} aria-valuenow={step}>
            <div className="quiz-progress-bar" style={{ width: `${pct}%` }} />
          </div>
          <div className="quiz-progress-label">
            {t(`Question ${step + 1} of ${total}`, `سؤال ${step + 1} من ${total}`)}
          </div>
        </header>

        {errorMsg && <p role="alert" className="quiz-feedback is-wrong">{errorMsg}</p>}
        {currentQ && (
          <div className="quiz-card" key={`${order[step]}-${step}`}>
            <p className="quiz-prompt">{currentQ.prompt}</p>
            <div className="quiz-options">
              {currentQ.options.map((opt, idx) => {
                const isPicked = picked === opt.key;
                const isCorrect = isPicked && feedback;
                // Only reveal the correctness of the user's own pick.
                // The correct answer is never highlighted while playing.
                const showRight = revealed && isPicked && isCorrect;
                const showWrong = revealed && isPicked && !isCorrect;
                return (
                  <button
                    type="button"
                    key={opt.key}
                    className={`quiz-option ${isPicked ? 'is-picked' : ''} ${showRight ? 'is-right' : ''} ${showWrong ? 'is-wrong' : ''} ${revealed ? 'is-revealed' : ''}`}
                    onClick={() => choose(opt.key)}
                    disabled={checking || (revealed && !isPicked)}
                  >
                    <span className="quiz-option-key">{opt.key}</span>
                    <span className="quiz-option-text">{opt.text}</span>
                    {showRight && (
                      <span className="quiz-option-icon" aria-label={t('Correct', 'صحيح')}>
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="5 12 10 17 19 7" />
                        </svg>
                      </span>
                    )}
                    {showWrong && (
                      <span className="quiz-option-icon" aria-label={t('Wrong', 'خطأ')}>
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="6" y1="6" x2="18" y2="18" />
                          <line x1="18" y1="6" x2="6" y2="18" />
                        </svg>
                      </span>
                    )}
                    <span className="quiz-option-num">{idx + 1}</span>
                  </button>
                );
              })}
            </div>

            {revealed && (
              <div className={`quiz-feedback ${feedback ? 'is-right' : 'is-wrong'}`}>
                <span className="quiz-feedback-text">
                  {feedback
                    ? t('Correct!', 'صحيح!')
                    : t('Not quite.', 'ليست صحيحة.')}
                </span>
                <button type="button" className="quiz-next" onClick={next} disabled={checking}>
                  {step + 1 >= total ? t('See results', 'النتيجة') : t('Next question', 'السؤال التالي')}
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points={isRtl ? '11 6 5 12 11 18' : '13 6 19 12 13 18'} />
                  </svg>
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
